import Eventable from 'utils/eventable';
import { copyProperties } from 'utils/mixin';
import type { Player } from '../../../../types';
import type { ConfigModel } from '../playback/config';

export type InternalPlayer = Player & {
    config: ConfigModel;
    // `isScrubbing()`/`setScrubbing()` are deliberately absent from the
    // public `Player` type (types/index.d.ts) - they are internal API for
    // first-party skins and plugins, not part of the published contract.
    //
    // Only `isScrubbing()` is exposed here, read-only: no in-repo plugin
    // under src/js/player/plugins/ sets the flag - every one of them is a
    // read-only consumer of it. The one plugin that does set it is a
    // first-party skin living in the separate `player-extras` repo; it
    // declares the player surface for itself (both members, optional, to
    // tolerate deployment version skew) in
    // player-extras/src/types/svp-player.d.ts and so never consults
    // `InternalPlayer` at all. `setScrubbing` is omitted here for that
    // reason, not because no plugin could legitimately need it - an
    // in-repo plugin that does would add it.
    isScrubbing(): boolean;
};

class PluginModel<TOpts = undefined> extends Eventable {
    player: InternalPlayer;
    el: null | HTMLElement;
    constructor(options: TOpts) {
        super();

        this.player = null!;
        this.el = null;

        if (typeof this.initialize === 'function') {
            this.initialize(options);
        }
    }

    getName() {
        throw new Error('getName() method has to be implemented');
    }

    initialize(_options: TOpts) {}

    setup(_player: Player) {}

    destroy() {}

    setPlayer(player: InternalPlayer) {
        this.player = player;
        this.setup(player);
    }
}

const createClass = <TImpl, TOpts>(plugin: TImpl) => {
    class CustomPluginModel extends PluginModel<TOpts> {}
    copyProperties(CustomPluginModel.prototype, plugin);
    return CustomPluginModel as unknown as PluginModel<TOpts> & TImpl;
};

export { createClass, PluginModel };
